import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import { Building, Upload, Save, Warehouse as WarehouseIcon, PackagePlus, History, GitMerge } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const SECTIONS = {
  Identity: ["company_name", "legal_name", "tagline"],
  Statutory: ["gstin", "pan", "cin", "tan", "udyam", "state_code"],
  Address: ["address_line1", "address_line2", "city", "state", "country", "pincode"],
  Contact: ["phone", "alt_phone", "email", "website"],
  Bank: null, // rendered separately
  Defaults: ["default_invoice_terms", "default_invoice_notes", "default_currency"],
  "GST E-Invoice API": [
    "einvoice_api_provider", "einvoice_gstin", "einvoice_client_id", "einvoice_client_secret",
    "einvoice_base_url", "einvoice_auth_url", "einvoice_irn_url", "einvoice_cancel_url", "einvoice_ewb_url",
    "einvoice_gsp_username", "einvoice_gsp_password", "einvoice_auth_token", "einvoice_token_expires_at",
  ],
};

const LABELS = {
  company_name: "Company Name *",
  legal_name: "Legal Name",
  tagline: "Tagline",
  gstin: "GSTIN",
  pan: "PAN",
  cin: "CIN",
  tan: "TAN",
  udyam: "UDYAM No.",
  state_code: "GST State Code (2-digit)",
  address_line1: "Address Line 1",
  address_line2: "Address Line 2",
  city: "City",
  state: "State",
  country: "Country",
  pincode: "PIN Code",
  phone: "Phone",
  alt_phone: "Alt Phone",
  email: "Email",
  website: "Website",
  default_invoice_terms: "Default Invoice Terms",
  default_invoice_notes: "Default Notes / Remarks",
  default_currency: "Default Currency",
  einvoice_api_provider: "API Provider (ClearTax / Masters India / IRIS / TaxPro)",
  einvoice_gstin: "GSTIN registered with GSP",
  einvoice_client_id: "Client ID",
  einvoice_client_secret: "Client Secret",
  einvoice_base_url: "Base URL",
  einvoice_auth_url: "Auth (Token) URL",
  einvoice_irn_url: "IRN Generation URL",
  einvoice_cancel_url: "IRN Cancel URL",
  einvoice_ewb_url: "e-Way Bill URL",
  einvoice_gsp_username: "GSP Username",
  einvoice_gsp_password: "GSP Password",
  einvoice_auth_token: "Current Auth Token (auto-refreshed)",
  einvoice_token_expires_at: "Token Expires At",
};

export default function CompanySetupPage() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const logoInput = useRef(null);
  const signInput = useRef(null);

  useEffect(() => { api.get("/company-settings").then(r => setForm(r.data)); }, []);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function setBank(k, v) { setForm({ ...form, bank: { ...(form.bank || {}), [k]: v } }); }

  async function save(e) {
    e.preventDefault();
    if (!form.company_name) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      await api.put("/company-settings", form);
      toast.success("Company settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function uploadAsset(file, asset_type) {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Max 2 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("asset_type", asset_type);
    try {
      const r = await api.post("/company-settings/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, [r.data.field]: r.data.url }));
      window.dispatchEvent(new CustomEvent("company-settings-updated"));
      toast.success(`${asset_type} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  }

  if (!form) return <div className="text-sm text-slate-500">Loading…</div>;

  const backendBase = (api.defaults.baseURL || "").replace(/\/api\/?$/, "");

  return (
    <div data-testid="company-setup-page">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Administration"
        title="Company Setup"
        subtitle="Configure your company identity, statutory codes, address, bank, and invoice defaults. These fields auto-populate every Sales / Purchase invoice header and PDF export."
        actions={
          <button data-testid="save-company-btn" form="company-form" disabled={saving} type="submit" className="bg-[#F97316] hover:bg-[#EA580C] text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Save size={14} /> {saving ? "Saving…" : "Save Settings"}
          </button>
        }
      />

      <form id="company-form" onSubmit={save} className="space-y-5">
        {/* Logo + Signature */}
        <Card>
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-2"><Building size={12} /> Brand Assets</h3>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider">Company Logo</div>
              <div className="border border-dashed border-slate-300 p-4 flex items-center gap-4">
                {form.logo_url ? (
                  <img src={form.logo_url.startsWith("http") ? form.logo_url : `${backendBase}${form.logo_url}`} alt="logo" className="w-24 h-24 object-contain bg-slate-50 border border-slate-200" />
                ) : (
                  <div className="w-24 h-24 bg-slate-100 flex items-center justify-center text-[10px] text-slate-400 uppercase tracking-wider">No logo</div>
                )}
                <div>
                  <input ref={logoInput} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={e => uploadAsset(e.target.files?.[0], "logo")} className="hidden" data-testid="logo-file" />
                  <button type="button" onClick={() => logoInput.current?.click()} className="bg-slate-900 text-white px-3 py-2 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5 hover:bg-slate-800"><Upload size={12} /> Upload Logo</button>
                  <div className="text-[10px] text-slate-500 mt-2">PNG / JPG / WEBP / SVG · Max 2 MB</div>
                </div>
              </div>
            </div>
            <div>
              <div className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider">Authorised Signature (for PDF)</div>
              <div className="border border-dashed border-slate-300 p-4 flex items-center gap-4">
                {form.signature_url ? (
                  <img src={form.signature_url.startsWith("http") ? form.signature_url : `${backendBase}${form.signature_url}`} alt="sign" className="w-24 h-24 object-contain bg-slate-50 border border-slate-200" />
                ) : (
                  <div className="w-24 h-24 bg-slate-100 flex items-center justify-center text-[10px] text-slate-400 uppercase tracking-wider">No sign</div>
                )}
                <div>
                  <input ref={signInput} type="file" accept="image/png,image/jpeg,image/webp" onChange={e => uploadAsset(e.target.files?.[0], "signature")} className="hidden" data-testid="signature-file" />
                  <button type="button" onClick={() => signInput.current?.click()} className="bg-slate-900 text-white px-3 py-2 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5 hover:bg-slate-800"><Upload size={12} /> Upload Signature</button>
                  <div className="text-[10px] text-slate-500 mt-2">PNG / JPG transparent preferred</div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {Object.entries(SECTIONS).map(([sec, fields]) => (
          <Card key={sec}>
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">{sec}</h3>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sec === "Bank" ? (
                <>
                  <Field label="Bank Name"><input value={form.bank?.bank_name || ""} onChange={e => setBank("bank_name", e.target.value)} className={inputCls} /></Field>
                  <Field label="Account Holder"><input value={form.bank?.account_holder || ""} onChange={e => setBank("account_holder", e.target.value)} className={inputCls} /></Field>
                  <Field label="Account Number"><input value={form.bank?.account_number || ""} onChange={e => setBank("account_number", e.target.value)} className={inputCls} /></Field>
                  <Field label="IFSC"><input value={form.bank?.ifsc || ""} onChange={e => setBank("ifsc", e.target.value)} className={inputCls} /></Field>
                  <Field label="Branch"><input value={form.bank?.branch || ""} onChange={e => setBank("branch", e.target.value)} className={inputCls} /></Field>
                  <Field label="Account Type">
                    <select value={form.bank?.account_type || "Current"} onChange={e => setBank("account_type", e.target.value)} className={inputCls}>
                      {["Current", "Savings", "OD", "CC"].map(t => <option key={t}>{t}</option>)}
                    </select>
                  </Field>
                </>
              ) : (
                fields.map(f => (
                  <Field key={f} label={LABELS[f] || f}>
                    {["default_invoice_terms", "default_invoice_notes"].includes(f) ? (
                      <textarea rows={3} value={form[f] || ""} onChange={e => set(f, e.target.value)} className={inputCls} data-testid={`field-${f}`} />
                    ) : (
                      <input value={form[f] || ""} onChange={e => set(f, e.target.value)} className={inputCls} data-testid={`field-${f}`} />
                    )}
                  </Field>
                ))
              )}
            </div>
          </Card>
        ))}

        {/* Sprint 2026-Jun — VoltLedger integration: Receivables settings */}
        <Card>
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Receivables Settings</h3>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Due Days After SES Approval (default 30)">
              <input data-testid="field-receivables-due-days" type="number" min="1"
                value={form.receivables_due_days_after_ses ?? 30}
                onChange={e => set("receivables_due_days_after_ses", e.target.value === "" ? 30 : Number(e.target.value))}
                className={inputCls} />
            </Field>
            <Field label="Aging Buckets (days, comma separated)">
              <input data-testid="field-receivables-aging-buckets"
                value={Array.isArray(form.receivables_aging_buckets) ? form.receivables_aging_buckets.join(", ") : (form.receivables_aging_buckets || "30, 60, 90")}
                onChange={e => set("receivables_aging_buckets", e.target.value)}
                onBlur={e => {
                  const arr = e.target.value.split(",").map(s => parseInt(s.trim(), 10)).filter(n => Number.isFinite(n) && n > 0);
                  set("receivables_aging_buckets", arr.length ? [...new Set(arr)].sort((a, b) => a - b) : [30, 60, 90]);
                }}
                placeholder="30, 60, 90" className={inputCls} />
            </Field>
            <Field label="Client Credit Days in Due-Date Chain">
              <label className="flex items-center gap-2 border border-slate-300 px-3 py-2.5 cursor-pointer">
                <input data-testid="field-receivables-use-credit-days" type="checkbox"
                  checked={form.receivables_use_client_credit_days !== false}
                  onChange={e => set("receivables_use_client_credit_days", e.target.checked)} />
                <span className="text-xs text-slate-600">Use client-specific Credit Days as due-date fallback</span>
              </label>
            </Field>
          </div>
        </Card>

        {/* Sprint 2026-Jul — Receivables consolidation: legacy module kill-switch */}
        <Card>
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Legacy Modules</h3>
          </div>
          <div className="p-5">
            <label className="flex items-start gap-3 border border-slate-300 px-4 py-3 cursor-pointer max-w-2xl">
              <input data-testid="field-legacy-pr-enabled" type="checkbox" className="mt-0.5"
                checked={form.legacy_payment_receipts_enabled === true}
                onChange={e => set("legacy_payment_receipts_enabled", e.target.checked)} />
              <span className="text-xs text-slate-600">
                <span className="font-bold text-slate-800 block mb-0.5">Enable legacy Payment Receipt module</span>
                Disabled by default — customer collections flow only through Receivables → Bulk Receipts.
                When off, the old Payment Receipt create/edit/delete APIs return 410 Gone and every blocked
                attempt is audit-logged. Historical receipts remain readable in reports and ledgers.
              </span>
            </label>
          </div>
        </Card>

        <div className="flex justify-end pt-2">
          <button data-testid="save-company-btn-bottom" disabled={saving} type="submit" className="bg-[#F97316] hover:bg-[#EA580C] text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Save size={14} /> {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </form>

      <BranchesCard />
      <WarehousesCard />
    </div>
  );
}

function BranchesCard() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);

  function load() { api.get("/branches").then(r => setList(r.data || [])); }
  useEffect(() => { load(); }, []);

  async function del(b) {
    if (b.is_default) { toast.error("Cannot delete the default branch"); return; }
    if (!window.confirm(`Delete branch "${b.branch_name}"?`)) return;
    await api.delete(`/branches/${b.id}`);
    toast.success("Deleted"); load();
  }

  async function makeDefault(b) {
    if (b.is_default) return;
    // unset others, set this one
    for (const x of list) if (x.is_default) await api.put(`/branches/${x.id}`, { is_default: false });
    await api.put(`/branches/${b.id}`, { is_default: true });
    toast.success(`"${b.branch_name}" is now the default branch`); load();
  }

  return (
    <Card className="mt-6" data-testid="branches-card">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building size={14} className="text-slate-500" />
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Branches</h3>
          <span className="text-[10px] text-slate-400">— add multiple branches; each can have its own GSTIN and serves as the seller of record on invoices</span>
        </div>
        <button data-testid="add-branch-btn" onClick={() => setEditing({
            branch_code: "", branch_name: "",
            legal_name: "", trade_name: "",
            gst_number: "", pan: "", cin: "", tan: "", udyam: "",
            state: "", state_code: "",
            address: "", address_line1: "", address_line2: "",
            city: "", country: "India", pincode: "",
            phone: "", alt_phone: "", email: "", website: "",
            bank: { bank_name: "", account_holder: "", account_number: "", ifsc: "", branch: "", account_type: "Current" },
            is_default: false, is_active: true,
          })}
          className="bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold">+ Add Branch</button>
      </div>
      <div className="p-5">
        {list.length === 0 ? (
          <div className="text-xs text-slate-400">No branches yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr>{["Code", "Branch Name", "GSTIN", "Phone", "Email", "Default", ""].map(h => (
                <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {list.map(b => (
                <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`branch-row-${b.branch_code}`}>
                  <td className="py-2 px-3 font-bold">{b.branch_code}</td>
                  <td className="py-2 px-3">{b.branch_name}</td>
                  <td className="py-2 px-3 tabular">{b.gst_number || "—"}</td>
                  <td className="py-2 px-3">{b.phone || "—"}</td>
                  <td className="py-2 px-3">{b.email || "—"}</td>
                  <td className="py-2 px-3">
                    {b.is_default ? (
                      <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 font-bold">Default</span>
                    ) : (
                      <button onClick={() => makeDefault(b)} className="text-[10px] text-slate-500 hover:text-[#F97316] underline">Make default</button>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button onClick={() => setEditing(b)} className="text-slate-500 hover:text-[#F97316] text-[10px] uppercase tracking-wider font-bold mr-3">Edit</button>
                    <button onClick={() => del(b)} className="text-slate-500 hover:text-red-600 text-[10px] uppercase tracking-wider font-bold">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && <BranchModal record={editing} onClose={() => { setEditing(null); load(); }} />}
    </Card>
  );
}

// Visual section heading inside the Branch modal (lives at module-level so
// React doesn't tear down the subtree on every render).
function BranchSection({ children }) {
  return (
    <div className="md:col-span-2 mt-2 mb-1 text-[10px] uppercase tracking-[0.2em] font-bold text-[#F97316] border-l-2 border-[#F97316] pl-3">{children}</div>
  );
}

function BranchModal({ record, onClose }) {
  const [form, setForm] = useState(record);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });
  // Bank fields are nested under `form.bank.*` (mirrors CompanyBank in Company Setup).
  const bank = form.bank || {};
  const setBank = (k, v) => setForm({ ...form, bank: { ...bank, [k]: v } });

  async function save(e) {
    e.preventDefault();
    if (!form.branch_code || !form.branch_name) { toast.error("Branch Code and Name are required"); return; }
    setSaving(true);
    try {
      if (form.id) await api.put(`/branches/${form.id}`, form);
      else await api.post("/branches", form);
      toast.success(form.id ? "Updated" : "Created");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={e => e.stopPropagation()}
            className="bg-white w-full max-w-3xl shadow-xl max-h-[92vh] overflow-y-auto flex flex-col"
            data-testid="branch-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-display text-lg font-bold">{form.id ? "Edit" : "New"} Branch</h3>
          <button type="button" onClick={onClose} aria-label="Close"
                  className="text-slate-400 hover:text-slate-700 text-xl leading-none px-1"
                  data-testid="branch-modal-close">×</button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <BranchSection>Identity</BranchSection>
          <Field label="Branch Code *"><input data-testid="branch-code" value={form.branch_code} onChange={e => set("branch_code", e.target.value.toUpperCase())} className={inputCls} placeholder="HO / MUM / DEL" /></Field>
          <Field label="Branch Name *"><input data-testid="branch-name" value={form.branch_name} onChange={e => set("branch_name", e.target.value)} className={inputCls} placeholder="Head Office — Mumbai" /></Field>
          <Field label="Legal Name"><input data-testid="branch-legal-name" value={form.legal_name || ""} onChange={e => set("legal_name", e.target.value)} className={inputCls} placeholder="As per GST registration" /></Field>
          <Field label="Trade Name"><input data-testid="branch-trade-name" value={form.trade_name || ""} onChange={e => set("trade_name", e.target.value)} className={inputCls} placeholder="Customer-facing name" /></Field>

          <BranchSection>Statutory</BranchSection>
          <Field label="GSTIN"><input data-testid="branch-gst" value={form.gst_number || ""} onChange={e => set("gst_number", e.target.value.toUpperCase())} className={inputCls + " tabular"} placeholder="27AAACR1234A1Z5" /></Field>
          <Field label="PAN"><input data-testid="branch-pan" value={form.pan || ""} onChange={e => set("pan", e.target.value.toUpperCase())} className={inputCls + " tabular"} placeholder="AAACR1234A" /></Field>
          <Field label="CIN"><input data-testid="branch-cin" value={form.cin || ""} onChange={e => set("cin", e.target.value.toUpperCase())} className={inputCls + " tabular"} placeholder="U40109DL2020PTC365432" /></Field>
          <Field label="TAN"><input data-testid="branch-tan" value={form.tan || ""} onChange={e => set("tan", e.target.value.toUpperCase())} className={inputCls + " tabular"} placeholder="DELR12345B" /></Field>
          <Field label="Udyam Registration"><input data-testid="branch-udyam" value={form.udyam || ""} onChange={e => set("udyam", e.target.value.toUpperCase())} className={inputCls + " tabular"} placeholder="UDYAM-XX-00-0000000" /></Field>
          <Field label="State Code (2-digit)"><input data-testid="branch-state-code" value={form.state_code || ""} onChange={e => set("state_code", e.target.value)} className={inputCls + " tabular"} placeholder="27" /></Field>

          <BranchSection>Address</BranchSection>
          <Field label="Address Line 1"><input data-testid="branch-addr1" value={form.address_line1 || ""} onChange={e => set("address_line1", e.target.value)} className={inputCls} /></Field>
          <Field label="Address Line 2"><input data-testid="branch-addr2" value={form.address_line2 || ""} onChange={e => set("address_line2", e.target.value)} className={inputCls} /></Field>
          <Field label="City"><input data-testid="branch-city" value={form.city || ""} onChange={e => set("city", e.target.value)} className={inputCls} /></Field>
          <Field label="State"><input data-testid="branch-state" value={form.state || ""} onChange={e => set("state", e.target.value)} className={inputCls} placeholder="Odisha / Maharashtra / …" /></Field>
          <Field label="Pincode"><input data-testid="branch-pincode" value={form.pincode || ""} onChange={e => set("pincode", e.target.value)} className={inputCls + " tabular"} /></Field>
          <Field label="Country"><input data-testid="branch-country" value={form.country || "India"} onChange={e => set("country", e.target.value)} className={inputCls} /></Field>
          <div className="md:col-span-2">
            <Field label="Address (free-text · legacy / printed on documents)"><textarea data-testid="branch-address" rows={2} value={form.address || ""} onChange={e => set("address", e.target.value)} className={inputCls} placeholder="Composed line for PDFs (leave blank to auto-compose from structured fields above)" /></Field>
          </div>

          <BranchSection>Contact</BranchSection>
          <Field label="Phone"><input data-testid="branch-phone" value={form.phone || ""} onChange={e => set("phone", e.target.value)} className={inputCls} /></Field>
          <Field label="Alt Phone"><input data-testid="branch-alt-phone" value={form.alt_phone || ""} onChange={e => set("alt_phone", e.target.value)} className={inputCls} /></Field>
          <Field label="Email"><input data-testid="branch-email" type="email" value={form.email || ""} onChange={e => set("email", e.target.value)} className={inputCls} /></Field>
          <Field label="Website"><input data-testid="branch-website" value={form.website || ""} onChange={e => set("website", e.target.value)} className={inputCls} placeholder="www.example.com" /></Field>

          <BranchSection>Bank Details (used on Tax Invoices issued under this branch)</BranchSection>
          <Field label="Bank Name"><input data-testid="branch-bank-name" value={bank.bank_name || ""} onChange={e => setBank("bank_name", e.target.value)} className={inputCls} /></Field>
          <Field label="Account Holder"><input data-testid="branch-bank-holder" value={bank.account_holder || ""} onChange={e => setBank("account_holder", e.target.value)} className={inputCls} /></Field>
          <Field label="Account Number"><input data-testid="branch-bank-acno" value={bank.account_number || ""} onChange={e => setBank("account_number", e.target.value)} className={inputCls + " tabular"} /></Field>
          <Field label="IFSC"><input data-testid="branch-bank-ifsc" value={bank.ifsc || ""} onChange={e => setBank("ifsc", e.target.value.toUpperCase())} className={inputCls + " tabular"} placeholder="HDFC0000000" /></Field>
          <Field label="Bank Branch"><input data-testid="branch-bank-branch" value={bank.branch || ""} onChange={e => setBank("branch", e.target.value)} className={inputCls} /></Field>
          <Field label="Account Type">
            <select data-testid="branch-bank-type" value={bank.account_type || "Current"} onChange={e => setBank("account_type", e.target.value)} className={inputCls}>
              <option>Current</option><option>Savings</option><option>OD / CC</option><option>Other</option>
            </select>
          </Field>

          <BranchSection>Settings</BranchSection>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={!!form.is_default} onChange={e => set("is_default", e.target.checked)} /> Default branch
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.is_active !== false} onChange={e => set("is_active", e.target.checked)} /> Active
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white z-10">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button data-testid="save-branch-btn" disabled={saving} type="submit" className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 text-xs uppercase tracking-wider font-bold">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}


// ============ Warehouses (Sprint 4.0 Phase A) ============
// Master list nested under Branches with admin-only "Seed Default
// Warehouses" recovery button calling POST /api/warehouses/seed-defaults.
function WarehousesCard() {
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [list, setList] = useState([]);
  const [kpis, setKpis] = useState({});   // {warehouse_id: {kpi fields}}
  const [branches, setBranches] = useState([]);
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [filterBranch, setFilterBranch] = useState("");
  // Sprint 4.5 — admin merge + audit log overlays
  const [merging, setMerging] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);

  async function load() {
    try {
      const [w, b, k] = await Promise.all([
        api.get("/warehouses", { params: { limit: 500 } }),
        api.get("/branches"),
        // Sprint 4.2 — bulk KPIs power the per-warehouse stat tiles.
        api.get("/warehouses/kpis/all"),
      ]);
      setList(w.data || []);
      setBranches(b.data || []);
      const map = {};
      for (const row of (k.data || [])) map[row.warehouse_id] = row;
      setKpis(map);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load warehouses");
    }
  }
  useEffect(() => { load(); }, []);

  async function del(w) {
    if (!window.confirm(`Delete warehouse "${w.warehouse_name}"?`)) return;
    try {
      await api.delete(`/warehouses/${w.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  }

  async function runSeedDefaults() {
    const ok = window.confirm(
      "Run the Default-Warehouse migration?\n\n" +
      "• Creates 'Main Store - <Branch>' for every branch missing one\n" +
      "• Stamps warehouse_id on legacy inventory_batches, stock_entries and assets\n" +
      "• Safe to rerun (idempotent — does nothing if everything is already migrated)",
    );
    if (!ok) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/warehouses/seed-defaults");
      const parts = [
        `Created: ${data.new_defaults_created ?? 0}`,
        `Branches with default: ${data.branches_with_default ?? 0}`,
        `Batches stamped: ${data.batches_stamped ?? 0}`,
        `Stock entries stamped: ${data.stock_entries_stamped ?? 0}`,
        `Assets stamped: ${data.assets_stamped ?? 0}`,
      ];
      toast.success(
        `Seed complete — ${parts.join(" · ")}`,
        { duration: 8000 },
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seed failed");
    } finally {
      setSeeding(false);
    }
  }

  const filtered = filterBranch
    ? list.filter(w => w.branch_id === filterBranch)
    : list;

  return (
    <Card className="mt-6" data-testid="warehouses-card">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <WarehouseIcon size={14} className="text-slate-500" />
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Warehouses</h3>
          <span className="text-[10px] text-slate-400">— physical stock locations nested under each branch (Phase A)</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="wh-filter-branch"
            value={filterBranch}
            onChange={e => setFilterBranch(e.target.value)}
            className="border border-slate-300 px-2 py-1.5 text-[11px] uppercase tracking-wider font-bold text-slate-600 bg-white"
          >
            <option value="">All branches</option>
            {branches.map(b => (
              <option key={b.id} value={b.id}>{b.branch_code} — {b.branch_name}</option>
            ))}
          </select>
          {isAdmin && (
            <button
              data-testid="view-warehouse-audit-log-btn"
              onClick={() => setAuditOpen(true)}
              title="Open the warehouse audit-log viewer (admin-only)."
              className="bg-slate-700 hover:bg-slate-800 text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5"
            >
              <History size={12} />
              View Audit Log
            </button>
          )}
          {isAdmin && (
            <button
              data-testid="seed-default-warehouses-btn"
              onClick={runSeedDefaults}
              disabled={seeding}
              title="Run the warehouse-master migration (admin-only). Idempotent."
              className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5 disabled:opacity-50"
            >
              <PackagePlus size={12} />
              {seeding ? "Seeding…" : "Seed Default Warehouses"}
            </button>
          )}
          <button
            data-testid="add-warehouse-btn"
            onClick={() => setEditing({
              warehouse_code: "", warehouse_name: "",
              branch_id: filterBranch || (branches[0]?.id || ""),
              address: "", store_incharge_name: "",
              is_active: true, is_default: false, remarks: "",
            })}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold"
          >
            + Add Warehouse
          </button>
        </div>
      </div>
      <div className="p-5">
        {filtered.length === 0 ? (
          <div className="text-xs text-slate-400" data-testid="warehouses-empty">
            No warehouses yet. Click <span className="font-bold text-slate-700">+ Add Warehouse</span> to create one
            {isAdmin && <span>, or use <span className="font-bold text-amber-700">Seed Default Warehouses</span> to auto-create &ldquo;Main Store - &lt;Branch&gt;&rdquo; for every branch.</span>}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr>{["Code", "Warehouse", "Branch", "Store In-Charge", "Inv Qty", "Inv Value", "Assets", "Last Receipt", "Active", "Default", ""].map(h => (
                <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {filtered.map(w => {
                const k = kpis[w.id] || {};
                const inrFmt = (n) => "₹" + new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n || 0);
                return (
                <tr key={w.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`warehouse-row-${w.warehouse_code}`}>
                  <td className="py-2 px-3 font-bold tabular">{w.warehouse_code}</td>
                  <td className="py-2 px-3">{w.warehouse_name}</td>
                  <td className="py-2 px-3 text-[12px] text-slate-600">{w.branch_name || "—"}</td>
                  <td className="py-2 px-3 text-[12px] text-slate-600">{w.store_incharge_name || <span className="text-amber-600 font-bold" data-testid={`wh-no-incharge-${w.warehouse_code}`}>— missing —</span>}</td>
                  <td className="py-2 px-3 text-[12px] tabular text-slate-700" data-testid={`wh-inv-qty-${w.warehouse_code}`}>{k.inventory_quantity ?? 0}</td>
                  <td className="py-2 px-3 text-[12px] tabular font-bold text-slate-800" data-testid={`wh-inv-value-${w.warehouse_code}`}>{inrFmt(k.inventory_value)}</td>
                  <td className="py-2 px-3 text-[12px] tabular text-slate-700" data-testid={`wh-asset-count-${w.warehouse_code}`}>
                    {k.asset_count ?? 0}
                    {k.asset_value > 0 ? <div className="text-[10px] text-slate-400">{inrFmt(k.asset_value)}</div> : null}
                  </td>
                  <td className="py-2 px-3 text-[11px] text-slate-500 font-mono" data-testid={`wh-last-receipt-${w.warehouse_code}`}>
                    {k.last_receipt_date || "—"}
                    {k.last_pi_number ? <div className="text-[9px] text-slate-400">{k.last_pi_number}</div> : null}
                  </td>
                  <td className="py-2 px-3">
                    {w.is_active ? (
                      <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 font-bold">Active</span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-wider bg-slate-200 text-slate-600 px-1.5 py-0.5 font-bold">Inactive</span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {w.is_default ? (
                      <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 font-bold">Default</span>
                    ) : <span className="text-[10px] text-slate-300">—</span>}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button onClick={() => setEditing(w)} data-testid={`wh-edit-${w.warehouse_code}`}
                      className="text-slate-500 hover:text-[#F97316] text-[10px] uppercase tracking-wider font-bold mr-3">Edit</button>
                    {w.is_active ? (
                      <button data-testid={`wh-inactivate-${w.warehouse_code}`}
                        onClick={async () => {
                          if (!window.confirm(`Inactivate warehouse "${w.warehouse_name}"? It will hide from pickers but keep all history.`)) return;
                          try {
                            await api.post(`/warehouses/${w.id}/inactivate`);
                            toast.success("Inactivated"); load();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Inactivate failed");
                          }
                        }}
                        className="text-slate-500 hover:text-amber-700 text-[10px] uppercase tracking-wider font-bold mr-3">Inactivate</button>
                    ) : (
                      <button data-testid={`wh-activate-${w.warehouse_code}`}
                        onClick={async () => {
                          try {
                            await api.post(`/warehouses/${w.id}/activate`);
                            toast.success("Activated"); load();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Activate failed");
                          }
                        }}
                        className="text-slate-500 hover:text-emerald-700 text-[10px] uppercase tracking-wider font-bold mr-3">Activate</button>
                    )}
                    {isAdmin && (
                      <button data-testid={`wh-merge-${w.warehouse_code}`}
                        onClick={() => setMerging(w)}
                        className="text-slate-500 hover:text-blue-700 text-[10px] uppercase tracking-wider font-bold mr-3">Merge</button>
                    )}
                    <button onClick={() => del(w)} data-testid={`wh-delete-${w.warehouse_code}`}
                      className="text-slate-500 hover:text-red-600 text-[10px] uppercase tracking-wider font-bold">Delete</button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {editing && (
        <WarehouseModal
          record={editing}
          branches={branches}
          onClose={() => { setEditing(null); load(); }}
        />
      )}
      {merging && (
        <WarehouseMergeModal
          source={merging}
          allWarehouses={list}
          onClose={() => { setMerging(null); load(); }}
        />
      )}
      {auditOpen && (
        <WarehouseAuditLogModal
          warehouses={list}
          onClose={() => setAuditOpen(false)}
        />
      )}
    </Card>
  );
}

function WarehouseModal({ record, branches, onClose }) {
  const [form, setForm] = useState(record);
  const [saving, setSaving] = useState(false);
  // Sprint 4.5 — Warehouse Types fetched from /api/warehouse-types so the
  // dropdown stays in sync with the backend catalogue.
  const [whTypes, setWhTypes] = useState([
    "Main Store", "Asset Store", "Consumable Store",
    "Scrap Store", "Transit Store", "Repair Store", "Custom",
  ]);
  const set = (k, v) => setForm({ ...form, [k]: v });

  useEffect(() => {
    api.get("/warehouse-types").then(({ data }) => {
      if (Array.isArray(data?.types) && data.types.length) setWhTypes(data.types);
    }).catch(() => {});
  }, []);

  async function save(e) {
    e.preventDefault();
    if (!form.warehouse_code || !form.warehouse_name) {
      toast.error("Warehouse Code and Name are required");
      return;
    }
    if (!form.branch_id) {
      toast.error("Branch is mandatory");
      return;
    }
    setSaving(true);
    try {
      // branch_name is auto-filled server-side — don't send it
      const { branch_name: _bn, ...payload } = form;
      if (form.id) await api.put(`/warehouses/${form.id}`, payload);
      else await api.post("/warehouses", payload);
      toast.success(form.id ? "Updated" : "Created");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={save}
        onClick={e => e.stopPropagation()}
        className="bg-white w-full max-w-2xl shadow-xl max-h-[92vh] overflow-y-auto flex flex-col"
        data-testid="warehouse-modal"
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-display text-lg font-bold">{form.id ? "Edit" : "New"} Warehouse</h3>
          <button type="button" onClick={onClose} aria-label="Close"
                  className="text-slate-400 hover:text-slate-700 text-xl leading-none px-1"
                  data-testid="warehouse-modal-close">×</button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Warehouse Code *">
            <input
              data-testid="wh-code"
              value={form.warehouse_code}
              onChange={e => set("warehouse_code", e.target.value.toUpperCase())}
              className={inputCls + " tabular"}
              placeholder="MAIN-HO / WH-MUM-01"
            />
          </Field>
          <Field label="Warehouse Name *">
            <input
              data-testid="wh-name"
              value={form.warehouse_name}
              onChange={e => set("warehouse_name", e.target.value)}
              className={inputCls}
              placeholder="Main Store — Head Office"
            />
          </Field>
          <Field label="Branch *">
            <select
              data-testid="wh-branch"
              value={form.branch_id || ""}
              onChange={e => set("branch_id", e.target.value)}
              className={inputCls}
            >
              <option value="">— Select Branch —</option>
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.branch_code} — {b.branch_name}</option>
              ))}
            </select>
          </Field>
          <Field label="Store In-Charge (name)">
            <input
              data-testid="wh-incharge"
              value={form.store_incharge_name || ""}
              onChange={e => set("store_incharge_name", e.target.value)}
              className={inputCls}
              placeholder="e.g. Ramesh Kumar"
            />
          </Field>
          {/* Sprint 4.5 — Warehouse Type catalogue (closed set + 'Custom' override) */}
          <Field label="Warehouse Type">
            <select
              data-testid="wh-type"
              value={form.warehouse_type || ""}
              onChange={e => set("warehouse_type", e.target.value)}
              className={inputCls}
            >
              <option value="">— Select Type —</option>
              {whTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          {form.warehouse_type === "Custom" && (
            <Field label="Custom Type Name *">
              <input
                data-testid="wh-type-other"
                value={form.warehouse_type_other || ""}
                onChange={e => set("warehouse_type_other", e.target.value)}
                className={inputCls}
                placeholder="e.g. Cold Storage Unit"
              />
            </Field>
          )}
          <Field label="Storage Capacity">
            <div className="flex gap-1">
              <input
                data-testid="wh-capacity"
                type="number" step="0.01"
                value={form.storage_capacity || ""}
                onChange={e => set("storage_capacity", Number(e.target.value) || 0)}
                className={inputCls + " tabular flex-1"}
              />
              <select
                data-testid="wh-capacity-unit"
                value={form.storage_capacity_unit || ""}
                onChange={e => set("storage_capacity_unit", e.target.value)}
                className={inputCls + " w-24"}
              >
                <option value="">unit</option>
                <option value="sqft">sqft</option>
                <option value="sqm">sqm</option>
                <option value="m3">m³</option>
                <option value="pallets">pallets</option>
              </select>
            </div>
          </Field>
          <Field label="Contact Person">
            <input data-testid="wh-contact-person"
              value={form.contact_person || ""}
              onChange={e => set("contact_person", e.target.value)}
              className={inputCls} placeholder="Operational POC" />
          </Field>
          <Field label="Contact Number">
            <input data-testid="wh-contact-number"
              value={form.contact_number || ""}
              onChange={e => set("contact_number", e.target.value)}
              className={inputCls} placeholder="+91-..." />
          </Field>
          <Field label="Email">
            <input data-testid="wh-email" type="email"
              value={form.email || ""}
              onChange={e => set("email", e.target.value)}
              className={inputCls} placeholder="store@company.com" />
          </Field>
          <Field label="Latitude">
            <input data-testid="wh-lat" type="number" step="0.000001"
              value={form.latitude || ""}
              onChange={e => set("latitude", Number(e.target.value) || 0)}
              className={inputCls + " tabular"} placeholder="19.076" />
          </Field>
          <Field label="Longitude">
            <input data-testid="wh-lng" type="number" step="0.000001"
              value={form.longitude || ""}
              onChange={e => set("longitude", Number(e.target.value) || 0)}
              className={inputCls + " tabular"} placeholder="72.877" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Address / Location notes">
              <textarea
                data-testid="wh-address"
                rows={2}
                value={form.address || ""}
                onChange={e => set("address", e.target.value)}
                className={inputCls}
                placeholder="Plot / Building / Lane / City"
              />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Remarks">
              <textarea
                data-testid="wh-remarks"
                rows={2}
                value={form.remarks || ""}
                onChange={e => set("remarks", e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              data-testid="wh-active"
              checked={form.is_active !== false}
              onChange={e => set("is_active", e.target.checked)}
            />
            Active
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              data-testid="wh-default"
              checked={!!form.is_default}
              onChange={e => set("is_default", e.target.checked)}
            />
            Default warehouse for this branch
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white z-10">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button
            data-testid="save-warehouse-btn"
            disabled={saving}
            type="submit"
            className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 text-xs uppercase tracking-wider font-bold"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}


// ============ Warehouse Merge Modal (Sprint 4.5) ============
// Admin-only utility. Relocates every warehouse-aware record (inventory,
// assets, PIs, IVs, RVs, transfers, repairs, movement ledger) from the
// source warehouse onto a target warehouse under the SAME branch.
// Backend endpoint: POST /api/warehouse-merge
function WarehouseMergeModal({ source, allWarehouses, onClose }) {
  const [targetId, setTargetId] = useState("");
  const [deactivateSource, setDeactivateSource] = useState(true);
  const [usage, setUsage] = useState(null);
  const [saving, setSaving] = useState(false);

  // Same-branch + active + non-source targets only (UX rules mirror the
  // backend guards in `merge_warehouse`).
  const candidates = (allWarehouses || []).filter(w =>
    w.branch_id === source.branch_id
    && w.id !== source.id
    && w.is_active !== false,
  );

  useEffect(() => {
    api.get(`/warehouses/${source.id}/usage`)
      .then(({ data }) => setUsage(data))
      .catch(() => setUsage({ counts: {}, total: 0 }));
  }, [source.id]);

  const usageRows = usage?.counts || {};
  const totalRefs = usage?.total ?? 0;

  async function submit(e) {
    e.preventDefault();
    if (!targetId) {
      toast.error("Pick a target warehouse to merge into");
      return;
    }
    if (source.is_default) {
      toast.error("Source is the branch default. Pick another default first.");
      return;
    }
    const ok = window.confirm(
      `Merge "${source.warehouse_name}" → target?\n\n` +
      `${totalRefs} record(s) will be re-pointed onto the target warehouse.\n` +
      `Movement ledger entries are preserved (re-pointed, not deleted).\n` +
      (deactivateSource ? "Source will be deactivated after merge.\n" : "") +
      `\nProceed?`,
    );
    if (!ok) return;
    setSaving(true);
    try {
      const { data } = await api.post("/warehouse-merge", {
        source_warehouse_id: source.id,
        target_warehouse_id: targetId,
        deactivate_source_after: deactivateSource,
        confirm: true,
      });
      toast.success(
        `Merged · ${data.total_relocated || 0} record(s) relocated`,
        { duration: 7000 },
      );
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Merge failed");
    } finally {
      setSaving(false);
    }
  }

  const usageLabel = {
    inventory_batches:   "Inventory Batches",
    stock_entries:       "Stock Entries",
    assets:              "Assets",
    purchase_invoices:   "Purchase Invoices",
    issue_vouchers:      "Issue Vouchers",
    return_vouchers:     "Return Vouchers",
    warehouse_transfers: "Warehouse Transfers",
    asset_repairs:       "Asset Repairs",
    warehouse_movements: "Movement Ledger Entries",
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        className="bg-white w-full max-w-xl shadow-xl max-h-[92vh] overflow-y-auto flex flex-col"
        data-testid="warehouse-merge-modal"
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <GitMerge size={14} className="text-blue-600" />
            <h3 className="font-display text-lg font-bold">Merge Warehouse</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
                  className="text-slate-400 hover:text-slate-700 text-xl leading-none px-1"
                  data-testid="warehouse-merge-modal-close">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-slate-50 border border-slate-200 p-3 text-xs">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Source Warehouse</div>
            <div className="font-bold text-slate-800" data-testid="merge-source-name">
              {source.warehouse_code} — {source.warehouse_name}
            </div>
            <div className="text-[11px] text-slate-500">Branch: {source.branch_name || "—"}</div>
            {source.is_default && (
              <div className="mt-2 text-[11px] text-amber-700 font-bold">
                ⚠ This is the branch default. Re-assign default before merge.
              </div>
            )}
          </div>

          <Field label="Target Warehouse *">
            <select
              data-testid="merge-target-select"
              value={targetId}
              onChange={e => setTargetId(e.target.value)}
              className={inputCls}
            >
              <option value="">— Select target warehouse (same branch) —</option>
              {candidates.map(w => (
                <option key={w.id} value={w.id}>
                  {w.warehouse_code} — {w.warehouse_name}
                  {w.is_default ? " · Default" : ""}
                </option>
              ))}
            </select>
            {candidates.length === 0 && (
              <div className="mt-1 text-[11px] text-rose-600" data-testid="merge-no-targets">
                No active warehouse available in this branch to merge into.
                Create or activate another warehouse first.
              </div>
            )}
          </Field>

          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
              References that will be re-pointed
            </div>
            {usage === null ? (
              <div className="text-[11px] text-slate-400" data-testid="merge-usage-loading">Loading usage…</div>
            ) : totalRefs === 0 ? (
              <div className="text-[11px] text-emerald-700" data-testid="merge-usage-empty">
                No active references — this merge is a no-op (only the warehouse master row will change).
              </div>
            ) : (
              <table className="w-full text-xs border border-slate-200" data-testid="merge-usage-table">
                <tbody>
                  {Object.entries(usageRows)
                    .filter(([, n]) => (n || 0) > 0)
                    .map(([k, n]) => (
                      <tr key={k} className="border-b border-slate-100">
                        <td className="px-2 py-1.5 text-slate-600">{usageLabel[k] || k}</td>
                        <td className="px-2 py-1.5 text-right font-bold tabular text-slate-800"
                            data-testid={`merge-usage-${k}`}>{n}</td>
                      </tr>
                    ))}
                  <tr className="bg-slate-50">
                    <td className="px-2 py-1.5 font-bold text-slate-700">Total references</td>
                    <td className="px-2 py-1.5 text-right font-bold tabular text-slate-900"
                        data-testid="merge-usage-total">{totalRefs}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              data-testid="merge-deactivate-source"
              checked={deactivateSource}
              onChange={e => setDeactivateSource(e.target.checked)}
            />
            Deactivate source warehouse after merge (recommended)
          </label>

          <div className="bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-900">
            <div className="font-bold mb-1">Heads up</div>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Target must be in the same branch ({source.branch_name || "—"}).</li>
              <li>Movement ledger entries are re-pointed, not deleted — history stays intact.</li>
              <li>Merge is audit-logged. Action is irreversible from the UI.</li>
            </ul>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white z-10">
          <button type="button" onClick={onClose}
                  className="px-4 py-2 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-100">
            Cancel
          </button>
          <button
            data-testid="save-warehouse-merge-btn"
            disabled={saving || !targetId || candidates.length === 0}
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold"
          >
            {saving ? "Merging…" : "Confirm Merge"}
          </button>
        </div>
      </form>
    </div>
  );
}


// ============ Warehouse Audit Log Modal (Sprint 4.5) ============
// Admin-only read-only viewer over /api/warehouse-audit-log.
// Filters: warehouse · action · from / to date.
function WarehouseAuditLogModal({ warehouses, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    warehouse_id: "",
    action: "",
    from_date: "",
    to_date: "",
  });

  const ACTIONS = [
    "create", "edit", "activate", "inactivate",
    "merge", "set_default", "unset_default", "delete",
  ];

  async function load() {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      params.limit = 500;
      const { data } = await api.get("/warehouse-audit-log", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load audit log");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function diffSummary(before, after) {
    const out = [];
    const keys = new Set([
      ...Object.keys(before || {}),
      ...Object.keys(after || {}),
    ]);
    keys.forEach(k => {
      const b = before?.[k];
      const a = after?.[k];
      if (JSON.stringify(b) !== JSON.stringify(a)) {
        out.push({ k, b, a });
      }
    });
    return out.slice(0, 5);
  }

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        className="bg-white w-full max-w-5xl shadow-xl max-h-[92vh] overflow-y-auto flex flex-col"
        data-testid="warehouse-audit-log-modal"
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <History size={14} className="text-slate-600" />
            <h3 className="font-display text-lg font-bold">Warehouse Audit Log</h3>
            <span className="text-[11px] text-slate-400">read-only · {rows.length} entr{rows.length === 1 ? "y" : "ies"}</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
                  className="text-slate-400 hover:text-slate-700 text-xl leading-none px-1"
                  data-testid="warehouse-audit-log-modal-close">×</button>
        </div>

        <div className="p-5 border-b border-slate-100 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <Field label="Warehouse">
            <select
              data-testid="audit-filter-warehouse"
              value={filters.warehouse_id}
              onChange={e => setFilters({ ...filters, warehouse_id: e.target.value })}
              className={inputCls}
            >
              <option value="">All</option>
              {(warehouses || []).map(w => (
                <option key={w.id} value={w.id}>
                  {w.warehouse_code} — {w.warehouse_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Action">
            <select
              data-testid="audit-filter-action"
              value={filters.action}
              onChange={e => setFilters({ ...filters, action: e.target.value })}
              className={inputCls}
            >
              <option value="">All</option>
              {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>
          <Field label="From Date">
            <input
              type="date"
              data-testid="audit-filter-from-date"
              value={filters.from_date}
              onChange={e => setFilters({ ...filters, from_date: e.target.value })}
              className={inputCls}
            />
          </Field>
          <Field label="To Date">
            <input
              type="date"
              data-testid="audit-filter-to-date"
              value={filters.to_date}
              onChange={e => setFilters({ ...filters, to_date: e.target.value })}
              className={inputCls}
            />
          </Field>
          <button
            type="button"
            data-testid="audit-apply-filters-btn"
            disabled={loading}
            onClick={load}
            className="bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 text-[11px] uppercase tracking-wider font-bold disabled:opacity-50"
          >
            {loading ? "Loading…" : "Apply Filters"}
          </button>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="text-xs text-slate-400" data-testid="audit-loading">Loading audit log…</div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-slate-400" data-testid="audit-empty">
              No audit entries match the current filters.
            </div>
          ) : (
            <table className="w-full text-xs" data-testid="audit-table">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>{["Date / Time", "Warehouse", "Action", "User", "Changes"].map(h => (
                  <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const diffs = diffSummary(r.before, r.after);
                  return (
                    <tr key={r.id} className="border-b border-slate-100 align-top" data-testid={`audit-row-${r.id}`}>
                      <td className="py-2 px-3 text-[11px] font-mono text-slate-600">
                        {(r.created_at || "").replace("T", " ").slice(0, 19)}
                      </td>
                      <td className="py-2 px-3">
                        <div className="text-[12px] font-bold text-slate-800">{r.warehouse_code || "—"}</div>
                        <div className="text-[10px] text-slate-500">{r.warehouse_name || ""}</div>
                      </td>
                      <td className="py-2 px-3">
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 font-bold ${actionBadge(r.action)}`}>
                          {r.action}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-[11px] text-slate-700">
                        <div>{r.user_name || r.user_email || "—"}</div>
                        <div className="text-[10px] text-slate-400">{r.user_email}</div>
                      </td>
                      <td className="py-2 px-3 text-[11px] text-slate-700">
                        {r.action === "merge" && r.extra?.merged_into ? (
                          <div className="mb-1">
                            Merged into <span className="font-bold">
                              {r.extra.merged_into.warehouse_code} — {r.extra.merged_into.warehouse_name}
                            </span> · {r.extra.total_relocated || 0} record(s) relocated
                          </div>
                        ) : null}
                        {diffs.length === 0 ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <ul className="space-y-0.5">
                            {diffs.map((d, i) => (
                              <li key={i} className="font-mono text-[10px]">
                                <span className="text-slate-500">{d.k}:</span>{" "}
                                <span className="text-rose-600 line-through">{fmtVal(d.b)}</span>
                                {" → "}
                                <span className="text-emerald-700 font-bold">{fmtVal(d.a)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function actionBadge(action) {
  switch ((action || "").toLowerCase()) {
    case "create":        return "bg-emerald-100 text-emerald-700";
    case "edit":          return "bg-slate-200 text-slate-700";
    case "activate":      return "bg-emerald-100 text-emerald-700";
    case "inactivate":    return "bg-amber-100 text-amber-800";
    case "merge":         return "bg-blue-100 text-blue-700";
    case "set_default":   return "bg-amber-100 text-amber-800";
    case "unset_default": return "bg-slate-200 text-slate-700";
    case "delete":        return "bg-rose-100 text-rose-700";
    default:              return "bg-slate-100 text-slate-600";
  }
}

function fmtVal(v) {
  if (v === undefined || v === null || v === "") return "∅";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
